import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  isFavorite, 
  addToFavorites, 
  isInWatchlist,
  addToWatchlist 
} from '../lib/storage';

const HeroSection = ({ featuredMovies, onMovieSelect }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [currentMovie, setCurrentMovie] = useState(null);

  useEffect(() => {
    if (featuredMovies && featuredMovies.length > 0) {
      setCurrentMovie(featuredMovies[currentIndex]);
    }
  }, [featuredMovies, currentIndex]);

  useEffect(() => {
    if (isAutoPlaying && featuredMovies && featuredMovies.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => 
          prevIndex === featuredMovies.length - 1 ? 0 : prevIndex + 1
        );
      }, 6000); // Change every 6 seconds

      return () => clearInterval(interval);
    }
  }, [isAutoPlaying, featuredMovies]);

  const handleAddToFavorites = () => {
    if (currentMovie && !isFavorite(currentMovie.id)) {
      addToFavorites(currentMovie);
    }
  };

  const handleAddToWatchlist = () => {
    if (currentMovie && !isInWatchlist(currentMovie.id)) {
      addToWatchlist(currentMovie);
    }
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume autoplay after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  if (!featuredMovies || featuredMovies.length === 0 || !currentMovie) {
    return null;
  }

  const backdropUrl = currentMovie.backdrop_path
    ? `https://image.tmdb.org/t/p/w1280${currentMovie.backdrop_path}`
    : null;

  const year = currentMovie.release_date?.split('-')[0];

  return (
    <div className="relative h-[80vh] lg:h-[90vh] overflow-hidden">
      {/* Background Image with Parallax Effect */}
      <div className="absolute inset-0 transform scale-105">
        {backdropUrl && (
          <img
            src={backdropUrl}
            alt={currentMovie.title}
            className="w-full h-full object-cover transition-all duration-1000 ease-out"
            style={{
              filter: 'brightness(0.4) contrast(1.1)',
            }}
          />
        )}
        
        {/* Multiple Gradient Overlays for Cinematic Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50 dark:to-gray-900"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            
            {/* Movie Category Badge */}
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                FEATURED
              </div>
              <div className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                HD 4K
              </div>
              {year && (
                <div className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                  {year}
                </div>
              )}
            </div>

            {/* Movie Title with Cinematic Typography */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 drop-shadow-2xl leading-tight">
              <span className="bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">
                {currentMovie.title}
              </span>
            </h1>

            {/* Movie Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-white/90">
              {/* Rating */}
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.floor(currentMovie.vote_average / 2)
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-400'
                      }`}
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <span className="font-semibold text-xl">
                  {currentMovie.vote_average?.toFixed(1)}
                </span>
                <span className="text-white/70">
                  ({currentMovie.vote_count?.toLocaleString()} reviews)
                </span>
              </div>

              {/* Quality Badges */}
              <div className="flex items-center space-x-3">
                <div className="bg-green-600 px-3 py-1 rounded text-sm font-bold">
                  HD
                </div>
                <div className="border-2 border-white/50 px-3 py-1 rounded text-sm font-bold">
                  CC
                </div>
                <div className="bg-blue-600 px-3 py-1 rounded text-sm font-bold">
                  5.1
                </div>
              </div>
            </div>

            {/* Movie Description */}
            <p className="text-lg lg:text-xl text-white/95 mb-10 leading-relaxed max-w-2xl drop-shadow-lg">
              {currentMovie.overview}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              
              {/* Primary Play Button */}
              <Link
                to={`/movie/${currentMovie.id}`}
                className="group flex items-center space-x-3 bg-white text-black px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-200 transition-all duration-200 shadow-2xl hover:shadow-3xl transform hover:scale-105"
                onClick={() => onMovieSelect?.(currentMovie)}
              >
                <svg className="w-6 h-6 fill-current group-hover:scale-110 transition-transform duration-200" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                <span>Play Now</span>
              </Link>

              {/* More Info Button */}
              <Link
                to={`/Movie/${currentMovie.id}`}
                className="flex items-center space-x-3 bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/30 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>More Info</span>
              </Link>

              {/* Quick Actions */}
              <div className="flex items-center space-x-3">
                {/* Add to Favorites */}
                <button
                  onClick={handleAddToFavorites}
                  className="p-4 bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white rounded-full hover:bg-red-500/70 hover:border-red-400 transition-all duration-200 group"
                  title="Add to Favorites"
                >
                  <svg className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                </button>

                {/* Add to Watchlist */}
                <button
                  onClick={handleAddToWatchlist}
                  className="p-4 bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white rounded-full hover:bg-blue-500/70 hover:border-blue-400 transition-all duration-200 group"
                  title="Add to Watchlist"
                >
                  <svg className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                  </svg>
                </button>

                {/* Share Button */}
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: currentMovie.title,
                        text: currentMovie.overview,
                        url: window.location.href
                      });
                    }
                  }}
                  className="p-4 bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white rounded-full hover:bg-white/30 transition-all duration-200 group"
                  title="Share"
                >
                  <svg className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Additional Movie Info */}
            <div className="flex items-center space-x-8 text-white/70 text-sm">
              <span>Available in 4K Ultra HD</span>
              <span>•</span>
              <span>Dolby Atmos</span>
              <span>•</span>
              <span>HDR10</span>
            </div>
          </div>
        </div>
      </div>

      {/* Carousel Indicators */}
      {featuredMovies.length > 1 && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <div className="flex items-center space-x-3">
            {featuredMovies.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-12 h-3 bg-white rounded-full'
                    : 'w-3 h-3 bg-white/50 rounded-full hover:bg-white/80'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Navigation Arrows */}
      {featuredMovies.length > 1 && (
        <>
          <button
            onClick={() => goToSlide(currentIndex === 0 ? featuredMovies.length - 1 : currentIndex - 1)}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 p-3 bg-black/50 backdrop-blur-sm text-white rounded-full hover:bg-black/70 transition-all duration-200 opacity-0 group-hover:opacity-100"
            aria-label="Previous movie"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
            </svg>
          </button>

          <button
            onClick={() => goToSlide(currentIndex === featuredMovies.length - 1 ? 0 : currentIndex + 1)}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 p-3 bg-black/50 backdrop-blur-sm text-white rounded-full hover:bg-black/70 transition-all duration-200 opacity-0 group-hover:opacity-100"
            aria-label="Next movie"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </>
      )}

      {/* Autoplay Control */}
      <button
        onClick={() => setIsAutoPlaying(!isAutoPlaying)}
        className="absolute top-4 right-4 z-20 p-2 bg-black/50 backdrop-blur-sm text-white rounded-lg hover:bg-black/70 transition-all duration-200 text-sm"
        title={isAutoPlaying ? 'Pause autoplay' : 'Resume autoplay'}
      >
        {isAutoPlaying ? (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
        )}
      </button>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 right-8 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>

      {/* Cinematic Bottom Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 dark:from-gray-900 to-transparent"></div>
    </div>
  );
};

export default HeroSection;